import { useMemo } from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import styled from 'styled-components';

const BACKGROUND_ERROR = 'error';
const BACKGROUND_WARNING = 'warning';

export const StyledWrapper = styled.div<{
  $backgroundType: 'error' | 'warning' | null;
}>(({
  $backgroundType,

  theme: {
    colors,
    indents,
  },
}) => {
  switch ($backgroundType) {
    case BACKGROUND_ERROR:
      return {
        borderRadius: 4,
        margin: -indents.INDENT_1,
        padding: indents.INDENT_1,
        backgroundColor: colors.ERROR_BACKGROUND,
      };

    case BACKGROUND_WARNING:
      return {
        borderRadius: 4,
        margin: -indents.INDENT_1,
        padding: indents.INDENT_1,
        backgroundColor: colors.PROGRESS_BACKGROUND,
      };

    default:
      return {};
  }
});

export const StyledHelpMessage = styled.div(({
  theme: {
    colors,
    fonts,
  },
}) => ({
  ...fonts.CAPTION,
  marginTop: 4,
  color: colors.NEUTRAL_SECONDARY,
}));

export const StyledErrorMessage = styled.div(({
  theme: {
    colors,
    fonts,
  },
}) => ({
  ...fonts.CAPTION,
  marginTop: 4,
  color: colors.ERROR_DEFAULT,
}));

export const StyledWarningMessage = styled.div(({
  theme: {
    colors,
    fonts,
  },
}) => ({
  ...fonts.CAPTION,
  marginTop: 4,
  color: colors.WARNING_DEFAULT,
}));

export type FieldWrapperProps = {
  /**
   * Подсказки поля
   */
  help?: string[];
  /**
   * Ошибки поля
   */
  errors?: string[];
  /**
   * Предупреждения поля
   */
  warnings?: string[];
  /**
   * Менять ли фон в случае ошибки или предупреждения
   */
  showBackground?: boolean;

  /**
   * Поле
   */
  children?: ReactNode;
};

export function FieldWrapper({
  children = undefined,
  errors = undefined,
  help = undefined,
  showBackground = false,
  warnings = undefined,
}: FieldWrapperProps): ReactElement {
  const backgroundType = useMemo(() => {
    if (!showBackground) {
      return null;
    }

    if (errors && errors.length > 0) {
      return BACKGROUND_ERROR;
    }

    if (warnings && warnings.length > 0) {
      return BACKGROUND_WARNING;
    }

    return null;
  }, [showBackground, errors, warnings]);

  return (
    <StyledWrapper
      $backgroundType={backgroundType}
    >
      <div>
        {children}
      </div>

      {
        help && help.length > 0 && (
          <div>
            {
              help.map((helpItem, index) => (
                <StyledHelpMessage
                  key={index}
                >
                  {helpItem}
                </StyledHelpMessage>
              ))
            }
          </div>
        )
      }

      {
        errors && errors.length > 0 && (
          <div>
            {
              errors.map((error, index) => (
                <StyledErrorMessage
                  key={index}
                >
                  {error}
                </StyledErrorMessage>
              ))
            }
          </div>
        )
      }

      {
        warnings && warnings.length > 0 && (
          <div>
            {
              warnings.map((warning, index) => (
                <StyledWarningMessage
                  key={index}
                >
                  {warning}
                </StyledWarningMessage>
              ))
            }
          </div>
        )
      }
    </StyledWrapper>
  );
}
